import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * Silent analytics tracker for a material page.
 *  - fires one `material_view` per visit
 *  - fires one `material_depth_75` per visit when the referenced content
 *    container reaches 75% scroll depth
 *
 * Backend automatically ignores staff (admin/editor) traffic and deduplicates
 * events at the (material_id, event_type, visit_id) index level.
 */

const ANON_KEY = "anokina_visitor_id";

function ensureAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = "anon-" + (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return "anon-" + Math.random().toString(36).slice(2);
  }
}

function newVisitId() {
  return "v-" + (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
}

async function post(materialId, type, visitId, anonId) {
  try {
    await api.post(`/materials/${materialId}/events`, {
      event_type: type,
      visit_id: visitId,
      anonymous_visitor_id: anonId,
    });
  } catch { /* silent */ }
}

/**
 * @param {string} materialId
 * @param {React.RefObject<HTMLElement>} contentRef Container whose height defines "100%" of the material.
 */
export default function useMaterialAnalytics(materialId, contentRef) {
  const state = useRef({ viewed: false, depth75: false, visitId: null, anonId: null });

  // View event — fire once per mount when materialId is known.
  useEffect(() => {
    if (!materialId) return;
    if (state.current.viewed) return;
    state.current.visitId = newVisitId();
    state.current.anonId = ensureAnonId();
    state.current.viewed = true;
    post(materialId, "material_view", state.current.visitId, state.current.anonId);
  }, [materialId]);

  // Depth 75% event — one per visit.
  useEffect(() => {
    if (!materialId) return;
    let cancelled = false;

    const check = () => {
      if (cancelled || state.current.depth75) return;
      const el = contentRef?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight;
      if (total <= 0) return;
      // Viewport-relative bottom of content that is currently above the viewport bottom.
      const consumed = Math.min(total, Math.max(0, window.innerHeight - rect.top));
      if (consumed / total >= 0.75) {
        state.current.depth75 = true;
        post(materialId, "material_depth_75", state.current.visitId || newVisitId(), state.current.anonId);
      }
    };

    // Give the page a moment to layout the content, then attach scroll.
    const t = setTimeout(check, 400);
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [materialId, contentRef]);
}
